import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CANVAS_DROP_EVENT, LONG_PRESS_MS, moveSteps, movedTooFar } from '../model/rowDrag';
import type { CanvasDropDetail } from '../model/rowDrag';

interface Options {
  /** Table id (`data-grid-id` of the grid root): rows are moved only within it. */
  gridId: string;
  /** Payload type of a drop on the stage; undefined = rows cannot go onto the stage. */
  mime: string | undefined;
  /** Rows dragged when the grip of `key` is held: the selection if it contains it, else the row. */
  keysFor(key: string): string[];
  /** Current row order (for moving); undefined = rows cannot be moved here. */
  order: readonly string[] | undefined;
  /** Moves rows by one step (the grid's `onMoveRows`). */
  move?(keys: string[], delta: -1 | 1): void;
  /** Ghost text, e.g. "2 rows". */
  label(count: number): string;
}

interface Press {
  key: string;
  x: number;
  y: number;
  timer: number;
}

/**
 * Long-press drag of table rows on touch screens (U74). Returns pointer handlers for a row's grip
 * and the ghost that follows the finger. Mouse pointers keep the HTML5 drag of the grip.
 */
export function useTouchRowDrag(opts: Options): {
  gripProps(key: string): {
    onPointerDown(e: ReactPointerEvent<HTMLElement>): void;
    onPointerMove(e: ReactPointerEvent<HTMLElement>): void;
    onPointerUp(e: ReactPointerEvent<HTMLElement>): void;
    onPointerCancel(): void;
  };
  /** True while a touch press / drag is running (blocks the native drag of the grip). */
  active(): boolean;
  ghost: ReactNode;
} {
  const press = useRef<Press | null>(null);
  const [ghost, setGhost] = useState<{ keys: string[]; x: number; y: number } | null>(null);
  const ghostRef = useRef(ghost);
  const optsRef = useRef(opts);
  useLayoutEffect(() => {
    ghostRef.current = ghost;
    optsRef.current = opts;
  });

  useEffect(() => () => window.clearTimeout(press.current?.timer), []);
  // while dragging, iOS must neither scroll nor select: block touchmove (non-passive)
  const dragging = ghost !== null;
  useEffect(() => {
    if (!dragging) return;
    const block = (e: TouchEvent) => e.preventDefault();
    document.addEventListener('touchmove', block, { passive: false });
    return () => document.removeEventListener('touchmove', block);
  }, [dragging]);

  const reset = () => {
    window.clearTimeout(press.current?.timer);
    press.current = null;
    setGhost(null);
  };

  const drop = (keys: string[], x: number, y: number) => {
    const o = optsRef.current;
    const target = document.elementFromPoint(x, y);
    const canvas = target?.closest('.canvas-wrap');
    if (canvas && o.mime !== undefined) {
      const detail: CanvasDropDetail = {
        mime: o.mime,
        data: JSON.stringify(keys),
        clientX: x,
        clientY: y,
      };
      canvas.dispatchEvent(new CustomEvent(CANVAS_DROP_EVENT, { detail }));
      return;
    }
    const row = target?.closest('[data-row-key]');
    const inGrid = row?.closest('[data-grid-id]')?.getAttribute('data-grid-id') === o.gridId;
    if (!row || !inGrid || o.order === undefined || o.move === undefined) return;
    const steps = moveSteps(o.order, keys, row.getAttribute('data-row-key')!);
    for (let i = 0; i < Math.abs(steps); i += 1) o.move(keys, steps > 0 ? 1 : -1);
  };

  return {
    gripProps: (key) => ({
      onPointerDown(e) {
        if (e.pointerType === 'mouse') return;
        e.stopPropagation();
        const el = e.currentTarget;
        const { clientX: x, clientY: y, pointerId } = e;
        window.clearTimeout(press.current?.timer);
        const timer = window.setTimeout(() => {
          try {
            el.setPointerCapture(pointerId);
          } catch {
            // the pointer is gone already
          }
          navigator.vibrate?.(15);
          setGhost({ keys: optsRef.current.keysFor(key), x, y });
        }, LONG_PRESS_MS);
        press.current = { key, x, y, timer };
      },
      onPointerMove(e) {
        const g = ghostRef.current;
        if (g) {
          e.preventDefault();
          setGhost({ ...g, x: e.clientX, y: e.clientY });
        } else if (press.current && movedTooFar(press.current, { x: e.clientX, y: e.clientY })) {
          reset();
        }
      },
      onPointerUp(e) {
        const g = ghostRef.current;
        reset();
        if (g) drop(g.keys, e.clientX, e.clientY);
      },
      onPointerCancel: reset,
    }),
    active: () => press.current !== null,
    ghost:
      ghost === null
        ? null
        : createPortal(
            <div className="dg-touch-ghost" role="status" style={{ left: ghost.x, top: ghost.y }}>
              {opts.label(ghost.keys.length)}
            </div>,
            document.body,
          ),
  };
}
